import {
  areaKeysForView,
  WORLD_AREA_KEY,
  mergeBoard,
  mergeLatest,
  radiusOption,
  sponsoredInView,
  type BoardPost,
  type LatLng,
  type RadiusId,
} from '@talkofthetown/shared';
import { useMemo, useState } from 'react';
import { useAreaDocs } from './useAreaDocs';
import { useSponsored } from './useSponsored';

export interface Board {
  /** Every ranked post in view, most opened first (the board shows what fits). */
  posts: BoardPost[];
  latest: BoardPost[];
  /** Paid posts that reach this reader: whispered more, marked Sponsored. */
  sponsored: BoardPost[];
  /** The radius shown: the one picked, or World without a location. */
  radius: RadiusId;
  /** True while a new radius loads; the previous board stays on screen meanwhile. */
  stale: boolean;
  loaded: boolean;
}

/**
 * The viewer's board: merges the area documents covering their circle, keeping posts made since
 * `since` (the reader's time range). A quiet area stays quiet (the board says so and offers a wider
 * distance or a longer time); nothing widens by itself.
 */
export function useBoard(
  center: LatLng | null,
  requested: RadiusId,
  now: number,
  since: number,
): Board {
  const effective: RadiusId = center ? requested : 'world';
  const opt = radiusOption(effective);
  const lat = center?.lat;
  const lng = center?.lng;
  // The cells covering the circle, plus the World board for the international AI summaries, which
  // are news for everyone (mergeBoard keeps them in every view).
  const keys = useMemo(() => {
    const cells = areaKeysForView(
      lat === undefined || lng === undefined ? null : { lat, lng },
      opt.km,
      opt.precision,
    );
    return cells.includes(WORLD_AREA_KEY) ? cells : [...cells, WORLD_AREA_KEY];
  }, [lat, lng, opt.km, opt.precision]);
  const areas = useAreaDocs(keys);
  const { docs } = areas;
  const promotions = useSponsored();
  // The board shows once the paid posts are known too, so they're whispered from the start.
  const loaded = areas.loaded && promotions.loaded;

  const posts = useMemo(
    () => mergeBoard(docs, { center, radiusKm: opt.km, now, since, limit: Infinity }),
    [docs, center, opt.km, now, since],
  );
  const latest = useMemo(
    () => mergeLatest(docs, { center, radiusKm: opt.km, now, since, limit: 20 }),
    [docs, center, opt.km, now, since],
  );

  const sponsored = useMemo(
    () => sponsoredInView(promotions.tiles, { center, now }),
    [promotions.tiles, center, now],
  );

  // Hold the last loaded board while the next one loads, so the page doesn't flash empty.
  const [lastLoaded, setLastLoaded] = useState<{ posts: BoardPost[]; latest: BoardPost[] }>({
    posts: [],
    latest: [],
  });
  if (loaded && (lastLoaded.posts !== posts || lastLoaded.latest !== latest))
    setLastLoaded({ posts, latest });

  return {
    posts: loaded ? posts : lastLoaded.posts,
    latest: loaded ? latest : lastLoaded.latest,
    sponsored,
    radius: effective,
    stale: !loaded,
    loaded,
  };
}
